/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState, Fragment } from 'react';
import 'aframe';
import '../primitives/hotspot';

import AframeIcon from '../components/AframeIcon';
import AframeTitle from '../components/AframeTitle';
import HotspotModal from '../components/HotspotModal/HotspotModal';
import * as constants from '../../../constants';
import { useGetCloudinaryBlob, usePrevious } from '../../../hooks';

const defaultContentWidth = 6;

const HotspotImage = ({
  active,
  showBaseHotspot,
  assets,
  addAsset,
  actualWidth,
  actualHeight,
  contentSize,
  position,
  alt,
  src,
  mediaId,
  hotspotId,
  isBeingEdited,
  isDisabled,
  title,
  rotation,
  onClick,
  darkMode,
  showLabel,
  cameraPosition,
  tourId,
  tourName,
  aid,
  hidden,
  hideGlow,
  iconSize,
  outWorldDisplay,
  icon,
  iconSrc,
}) => {
  const imageBlob = useGetCloudinaryBlob(src);
  const prevImageBlob = usePrevious(imageBlob);
  const prevMediaId = usePrevious(mediaId);
  const [ratio, setRatio] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [centerHotspot, setCenterHotspot] = useState(false);

  const iconButtonRef = useRef();
  const aframeImageRef = useRef();

  const titleId = showLabel ? `title_${hotspotId}` : null;
  const contentId = `content_${hotspotId}`;
  const iconId = `icon_${hotspotId}`;
  const customIconId = `custom-icon_${hotspotId}`;
  const customAssetId = `custom-asset_${hotspotId}`;
  const focusId = `focus-outline_${hotspotId}`;
  const useCustomIcon = icon && iconSrc;

  let width;
  let height;

  const length =
    typeof contentSize?.size === 'number'
      ? contentSize?.size
      : defaultContentWidth;

  if (ratio >= 1) {
    width = length;
    height = length / ratio;
  } else {
    width = length * ratio;
    height = length;
  }

  const contentYPosition = height / 2 + 0.75;

  useEffect(() => {
    const iconButton = iconButtonRef && iconButtonRef.current;
    const aframeImage = aframeImageRef && aframeImageRef.current;
    const image = document.getElementById(mediaId);

    const handleOpen = () => {
      onClick(hotspotId, isDisabled);
      setCenterHotspot(true);
    };

    if (assets[mediaId] && assets[mediaId].loaded) {
      image && setImageLoaded(true);
      image && setRatio(image.naturalWidth / image.naturalHeight);
    }

    if (prevMediaId !== mediaId && aframeImage) {
      image && setImageLoaded(true);
    }

    if (prevImageBlob !== imageBlob) {
      setImageLoaded(false);
      addAsset(mediaId, imageBlob, alt || '');
    }

    if (actualHeight && actualWidth) {
      setRatio(actualWidth / actualHeight);
    }

    iconButton && iconButton.addEventListener('click', handleOpen);

    return () => {
      iconButton && iconButton.removeEventListener('click', handleOpen);
    };
  }, [
    actualHeight,
    actualWidth,
    addAsset,
    alt,
    assets,
    height,
    hotspotId,
    iconButtonRef,
    imageBlob,
    imageLoaded,
    isDisabled,
    mediaId,
    onClick,
    prevImageBlob,
    prevMediaId,
    ratio,
    src,
  ]);
  return (
    <Fragment>
      {assets[mediaId] && imageLoaded && (
        <Fragment>
          <a-entity rotation={rotation}>
            <a-hotspot
              darkmode={darkMode}
              underedits={isBeingEdited}
              disabled={isDisabled}
              position={position}
              content={contentId}
              visible={!hidden}
              icon={useCustomIcon ? customIconId : iconId}
              title={titleId}
              id={`hotspot_${hotspotId}`}
              translatecontent={`0 ${contentYPosition} 0.4`}
              active={active && showBaseHotspot}
              rotateto={cameraPosition}
              tourId={tourId}
              tourName={tourName}
              hotspotName={title}
              type={'image'}
              aid={aid}
              centerHotspot={centerHotspot}
              data-testid={`hotspot_${hotspotId}`}
              data-cy={`hotspot_${hotspotId}`}
              focusoutlineicon={focusId}
            >
              {!outWorldDisplay && (
                <a-image
                  id={contentId}
                  data-testid={contentId}
                  ref={aframeImageRef}
                  src={`#${mediaId}`}
                  width={width}
                  height={height}
                  position={`0 ${contentYPosition} -0.4`}
                  scale="1 1 1"
                  visible="false"
                  material="side: front"
                  align="center"
                  opacity="1"
                />
              )}
              {showLabel && (
                <AframeTitle
                  id={titleId}
                  data-testid={titleId}
                  title={title}
                  darkMode={darkMode}
                  iconSize={iconSize}
                />
              )}
              <AframeIcon
                focusId={focusId}
                darkMode={darkMode}
                forwardRef={iconButtonRef}
                src={
                  icon
                    ? `#${customAssetId}`
                    : `#hotspot__image-icon${darkMode ? '-black' : ''}`
                }
                id={icon ? customIconId : iconId}
                data-testid={icon ? customIconId : iconId}
                hideGlow={hideGlow}
                iconSize={iconSize}
              />
            </a-hotspot>
            {/* a custom icon that only applies to a single hotspot */}
            {useCustomIcon && (
              <a-asset>
                <img
                  src={iconSrc}
                  id={customAssetId}
                  data-testid={customAssetId}
                  crossOrigin="anonymous"
                  alt=""
                />
              </a-asset>
            )}
          </a-entity>
          {outWorldDisplay && (
            <HotspotModal
              img={src}
              name={title}
              alt={alt}
              closeModal={() => {
                onClick(hotspotId, isDisabled);
                setCenterHotspot(false);
              }}
              showModal={active && !showBaseHotspot}
              hotspotId={hotspotId}
              type={constants.IMAGES}
              isBeingEdited={isBeingEdited}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default HotspotImage;
